import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../../components/ui/button';
import { CdaLogo } from '../../../components/cda/CdaBrand';
import { LanguageBar } from '../../../i18n/cda';
import { CheckCircle, ArrowRight, Briefcase, FileText, MessageSquare, Award } from 'lucide-react';

const CdaJobReadiness = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <Helmet>
        <title>CDA Job Readiness Training | Employment Preparation for Early Childhood Educators</title>
        <meta name="description" content="Prepare for your early childhood career with CDA job readiness training. Learn interview skills, resume writing, workplace professionalism, and get connected with childcare employers in Texas." />
        <link rel="canonical" href="https://carecomplycda.com/cda-job-readiness" />
      </Helmet>

      <LanguageBar />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/cda"><CdaLogo size={34} /></Link>
          <Button onClick={() => navigate('/cda')} className="bg-[#0d7d6f] hover:bg-[#0a6358]">
            Start Your Journey
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="text-center mb-12">
          <p className="text-[#c4622d] font-semibold mb-2">Career Preparation</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            CDA Job Readiness Training
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Earning your CDA is just the beginning. Care Comply prepares you for success in the workplace with comprehensive job readiness training.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Why Job Readiness Matters</h2>
          <p className="text-slate-600 mb-4">
            Many CDA candidates complete their 120 hours of training but have never worked in a professional childcare setting. While you may have the knowledge, employers also look for workplace skills like professionalism, reliability, communication, and teamwork.
          </p>
          <p className="text-slate-600 mb-4">
            Care Comply's Workforce Readiness module helps bridge this gap with practical training on what employers expect and how to present yourself as a qualified, job-ready candidate.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">What You'll Learn</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-900">Professional Appearance</h3>
                <p className="text-sm text-slate-600">What to wear for interviews and in the classroom</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-900">Attendance & Reliability</h3>
                <p className="text-sm text-slate-600">Why showing up matters and how to handle schedule issues</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-900">Communication Skills</h3>
                <p className="text-sm text-slate-600">Professional communication with families and coworkers</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-900">Workplace Boundaries</h3>
                <p className="text-sm text-slate-600">Maintaining professionalism and confidentiality</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-900">Resume Building</h3>
                <p className="text-sm text-slate-600">How to create a resume that highlights your CDA training</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-900">Interview Success</h3>
                <p className="text-sm text-slate-600">Common questions and how to make a great impression</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Briefcase className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">11 Training Modules</h3>
            <p className="text-slate-600">Comprehensive training covering every aspect of workplace readiness for childcare professionals.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <FileText className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Resume Templates</h3>
            <p className="text-slate-600">Professional resume templates designed specifically for CDA candidates.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <MessageSquare className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Practice Questions</h3>
            <p className="text-slate-600">Common interview questions with sample answers to help you prepare.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Award className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Readiness Score</h3>
            <p className="text-slate-600">Take a 20-question assessment to measure your professional readiness.</p>
          </div>
        </div>

        <div className="bg-[#0d7d6f] rounded-2xl p-8 text-white text-center mb-8">
          <h2 className="text-2xl font-bold mb-3">Get Job-Ready with Care Comply</h2>
          <p className="mb-6 opacity-90">Employers want CDA candidates who are trained AND workplace-ready. We prepare you for both.</p>
          <Button onClick={() => navigate('/cda')} className="bg-white text-[#0d7d6f] hover:bg-slate-100">
            Start Training Today <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="text-center text-sm text-slate-500">
          <p>Care Comply serves San Antonio, Austin, Houston, Dallas, and all of Texas.</p>
          <p className="mt-2">
            <Link to="/cda-career-coaching-san-antonio" className="text-[#0d7d6f] hover:underline">Career Coaching</Link> • 
            <Link to="/cda-480-hours-support" className="text-[#0d7d6f] hover:underline ml-2">480-Hour Support</Link> • 
            <Link to="/cda-portfolio-help" className="text-[#0d7d6f] hover:underline ml-2">Portfolio Help</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default CdaJobReadiness;
